import { RULETYPE_PREPACK } from '../carts/util';

// Constants
/**
 * @name DEFAULT_PRODUCTS_INFO_STATE
 * @description default structure for product info item
 * @constant {Object}
 */
export const DEFAULT_PRODUCTS_INFO_STATE = {
  products: {},
  productCodesBeingFetched: []
};

/**
 * @name DEFAULT_PRODUCT_INFO_STRUCTURE
 * @description default structure for product info store
 * @constant {Object}
 */
export const DEFAULT_PRODUCT_INFO_STRUCTURE = {
  id: '',
  directShip: false,
  returnable: false,
  season: '',
  region: '',
  color: '',
  segment: '',
  dimensions: '',
  division: '',
  styleGenderAge: '',
  category: '',
  colorName: '',
  deliveryUnitOfMeasure: '',
  deliveryUnit: 0,
  prePackIndicator: false,
  prePackCode: 0,
  productRegionPromoIndicator: '',
  sport: '',
  silhouette: {
    description: '',
    code: ''
  },
  silhouetteType: {
    description: '',
    code: ''
  },
  productCode: '',
  team: {},
  name: '',
  copy: {
    name: '',
    language: '',
    description: ''
  },
  bom: {
    colors: {
      primary: ''
    }
  },
  offerDates: {
    endFuture: '',
    endProduct: '',
    firstProduct: ''
  },
  sizes: [],
  prices: {
    retail: 0,
    wholesale: 0,
    currency: '',
    priceListType: ''
  },
  images: {},
  legacyImages: []
};

// Functions
/**
 * @function mapProductInfoToRulesetList
 * @description generates rules from productInfo
 * @param {Object} productInfo
 * @return {Array} rules
 */
export const mapProductInfoToRulesetList = productInfo => {
  const rules = [];
  const { prePackIndicator, deliveryUnit, deliveryUnitOfMeasure } = productInfo;
  // Pre-Pack
  if (prePackIndicator && deliveryUnit && deliveryUnitOfMeasure) {
    rules.push({
      type: RULETYPE_PREPACK,
      data: {
        rule: deliveryUnitOfMeasure,
        value: deliveryUnit
      }
    });
  }

  return rules;
};

/**
 * @function normalizeProducts
 * @description normalizes products structure
 * @param {Object} params
 * @param {Array} params.styles the array of products to adjust
 * @return {Object} normalized product list
 */
export const normalizeProducts = ({ styles = [] }) => {
  const normalizedProducts = {};
  styles.forEach(({ styleNumber, products }) => {
    // the API can return several entries for one style number (grouped by promo indicator, etc.), so don't reset
    normalizedProducts[styleNumber] = normalizedProducts[styleNumber] || {};
    products.forEach(product => {
      const { styleColor = null, productCode = null } = product;
      const color = productCode ? productCode.split('-')[1] : styleColor.split('-')[1];
      const prices = product.prices[0];
      const images = {};
      product.images.forEach(({ type, ...rest }) => {
        images[type] = { type, ...rest };
      });
      const adjustedProductInfo = {
        ...DEFAULT_PRODUCT_INFO_STRUCTURE,
        ...product,
        name: product.copy.name,
        legacyImages: product.images,
        images,
        prices,
        rules: mapProductInfoToRulesetList(product),
        sizes: product.sizes.map(size => ({
          promoIndicator: size.promoIndicator,
          sizeCode: size.description,
          displaySize: size.displaySize,
          sizeConversions: size.sizeConversions,
          sortOrderNumber: size.sortOrderNumber,
          upc: size.upc
        }))
      };

      normalizedProducts[styleNumber][color] = adjustedProductInfo;
    });
  });
  return normalizedProducts;
};

/**
 * @function isFetchingProductInfo
 * @description check if product code is in list of being fetched
 * @param {Object} productsInfo the productsInfo store
 * @param {String} productCode the code to check for
 * @return {Boolean}
 */
export const isFetchingProductInfo = (productsInfo, productCode) =>
  productsInfo.productCodesBeingFetched.includes(productCode.split('-')[0]);

/**
 * @function findProductInfo
 * @description search for product in list
 * @param {Object} productInfoList the productsInfo List
 * @param {String} productCode the code to check for
 * @return {Object|Null}
 */
export const findProductInfo = (productInfoList, productCode) => {
  const [style, color] = productCode.split('-');

  if (style && color) {
    return productInfoList?.[style]?.[color] || null;
  }
  return null;
};

// Export
export default {
  findProductInfo,
  isFetchingProductInfo,
  normalizeProducts,
  DEFAULT_PRODUCTS_INFO_STATE,
  DEFAULT_PRODUCT_INFO_STRUCTURE
};
